import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiCard, UiCardHeader, UiCardBody, UiDataTable, UiTableFilters } from 'components/common'
import { Box, Button, Flex, Heading } from '@chakra-ui/react'
import { exportCustomerReport, filterProjects } from 'api'
import { useDebouncedCallback } from 'use-debounce'
import { getAssetUrl, PROJECT_STAGES } from 'lib'
import { useAppContext } from 'hooks/useAppContext'

const BreadcrumbNav = [
  {
    label: 'Customer Report',
    isCurrentPage: true
  }
]

export const CUSTOMER_REPORT_COLUMNS = [
  {
    label: 'ID',
    orderBy: 'id',
    render: ({ id }) => id
  },
  {
    label: 'Company',
    orderBy: 'customerCompany',
    render: ({ customerCompany }) => customerCompany
  },
  {
    label: 'Address',
    orderBy: 'customerFullAddress',
    render: ({ customerFullAddress }) => customerFullAddress
  },
  {
    label: 'Customer Name',
    orderBy: 'customerName',
    render: ({ customerName }) => customerName
  },
  {
    label: 'Salesperson',
    orderBy: 'salesUserName',
    render: ({ salesUserName }) => salesUserName
  },
  {
    label: 'Stage',
    orderBy: 'stage',
    render: ({ stage }) => stage
  },
  {
    label: 'View',
    render: ({ notesCount, onRemove, onNotes, onView, ...rest }) => (
      <Flex columnGap={2}>
        <Button size="xs" colorScheme="blue" onClick={() => onView(rest)}>View</Button>
      </Flex>
    )
  }
]

export const ProjectCustomerReport = () => {
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 50,
    search: '',
    orderBy: {
      key: 'id',
      direction: 'asc'
    }
  })
  const [filterFieldValues, setFilterFieldValues] = useState([]) // e.g. [{ field: 'fabRend', name: 'Job Packet', values: [{ value: 'Incomplete', count: 10 }] }]
  const [filterSelections, setFilterSelections] = useState({}) // e.g. { fabRend: ['Incomplete', 'Pending Review', 'Competed'] }
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [rows, setRows] = useState([])

  const load = async () => {
    setIsLoading(true)
    try {
      const salesUserIdsValues = filterFieldValues.filter(v => v.field === 'salesUserIds')[0]?.values || []
      const salesUserIds = filterSelections.salesUserIds?.length > 0 ? filterSelections.salesUserIds.map(v => salesUserIdsValues.filter(u => u.value === v)[0]?.salesUserId) : []
      const stage = filterSelections.stage?.length > 0 ? filterSelections.stage : [PROJECT_STAGES.Install, PROJECT_STAGES.PostInstall, PROJECT_STAGES.Complete]
      const response = await filterProjects(
        'All',
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction,
        ['salesUserIds', 'stage'],
        { salesUserIds, stage }
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setFilterFieldValues(response.filterFieldValues)
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  const debouncedLoad = useDebouncedCallback(load, 400)

  useEffect(() => {
    debouncedLoad()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction, filterSelections])

  const onView = (project) => {
    window.open(`/projects/detail/${project.id}`, '_blank')
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, onView }))
  }, [rows])

  const updateFilterSelections = (field, value) => {
    const newFilterSelections = { ...filterSelections }
    if (newFilterSelections[field]) {
      if (newFilterSelections[field].includes(value)) {
        newFilterSelections[field] = newFilterSelections[field].filter(v => v !== value)
      } else {
        newFilterSelections[field].push(value)
      }
    } else {
      newFilterSelections[field] = [value]
    }
    setFilterSelections(newFilterSelections)
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const salesUserIdsValues = filterFieldValues.filter(v => v.field === 'salesUserIds')[0]?.values || []
      const salesUserIds = filterSelections.salesUserIds?.length > 0 ? filterSelections.salesUserIds.map(v => salesUserIdsValues.filter(u => u.value === v)[0]?.salesUserId) : []
      const stage = filterSelections.stage?.length > 0 ? filterSelections.stage : [PROJECT_STAGES.Install, PROJECT_STAGES.PostInstall, PROJECT_STAGES.Complete]
      const { path } = await exportCustomerReport({ salesUserIds, stage })
      const a = document.createElement('a')
      a.href = getAssetUrl(path)
      a.download = 'customer-report.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (err) {
      console.log(err)
      createToast('error', 'Error', 'Failed to export as csv.')
    }
    setIsExporting(false)
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Customer Report</Heading>
        </Flex>
        <UiCard borderRadius={0}>
          <UiCardHeader bg="gray.500" p={4}>
            <Flex gap={2}>
              <Button
                border={1}
                borderColor="gray.200"
                borderStyle="solid"
                colorScheme="blue"
                size="xs"
                borderRadius="4px"
                onClick={handleExport}
                isLoading={isExporting}
              >
                Export as CSV
              </Button>
            </Flex>
          </UiCardHeader>
          <UiCardBody p={8}>
            {filterFieldValues.length > 0 && (
              <UiTableFilters
                filterFieldValues={filterFieldValues}
                filterSelections={filterSelections}
                updateFilterSelections={updateFilterSelections}
                mb={4}
              />
            )}
            <UiDataTable
              tableConfig={tableConfig}
              updateTableConfig={setTableConfig}
              rows={customizedRows}
              rowKey="id"
              columns={CUSTOMER_REPORT_COLUMNS}
              isLoading={isLoading}
            />
          </UiCardBody>
        </UiCard>
      </Box>
    </>
  )
}
